import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { WindowLevel, WindowLevels } from '@/modules/Editor/imageManipulation'
import type { PresetPayload } from './imageManipulationTypes'
import type { PartialRecord } from '@/core/helperTypes'

export const useImageManipulationPresetsStore = defineStore('imageManipulationPresets', () => {
  const presets = ref<PresetPayload[]>([])

  const activePresetIdPerSlot = ref<PartialRecord<string, PresetPayload['id']>>({})

  const currentSlotName = ref<string>('')

  const activePresetId = computed({
    get: () =>
      currentSlotName.value ? activePresetIdPerSlot.value[currentSlotName.value] : undefined,
    set: (value: PresetPayload['id'] | undefined) => {
      if (currentSlotName.value) {
        activePresetIdPerSlot.value = {
          ...activePresetIdPerSlot.value,
          [currentSlotName.value]: value,
        }
      }
    },
  })

  /**
   * Preset names have the following format: "Name (Level/Width)"
   */
  const getPresetValuesFromName = (value: string): WindowLevel => {
    const [, width, level] = value.match(/\((-?\d+)\/(-?\d+)\)/) || [null, '30', '1']
    return { width: Number(width), level: Number(level) }
  }

  const getPresetIdByValues = (values?: WindowLevels): number | undefined => {
    if (!values) {
      return undefined
    }

    return presets.value.find(({ name, manipulation: { windowLevels } }) => {
      const { width, level } = getPresetValuesFromName(name)
      return (
        (width === values[0] && level === values[1]) ||
        (windowLevels[0] === values[0] && windowLevels[1] === values[1])
      )
    })?.id
  }

  const getActivePresetIdForSlot = (slotName: string): PresetPayload['id'] =>
    activePresetIdPerSlot.value[slotName]

  const setCurrentSlotName = (slotName: string): void => {
    currentSlotName.value = slotName
  }

  const clearAllPresets = (): void => {
    activePresetIdPerSlot.value = {}
  }

  return {
    presets,
    activePresetId,
    activePresetIdPerSlot,
    currentSlotName,
    getPresetValuesFromName,
    getPresetIdByValues,
    getActivePresetIdForSlot,
    setCurrentSlotName,
    clearAllPresets,
  }
})
